import { useRef, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useQueryClient } from '@tanstack/react-query';
import { saveAs } from 'file-saver';
import { XIcon, DownloadIcon } from 'lucide-react';
import Papa from 'papaparse';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { toast } from 'sonner';
import * as yup from 'yup';

import { datasourceEndpoints } from '@/api/endpoints';
import useFaqSubmit from '@/components/atoms/FileUpload/useFaqSubmit';
import {
  SidebarWrapper,
  SidebarWrapperTitle,
} from '@/components/atoms/SidebarWrapper/SidebarWrapper';
import { UnsavedChangesModal } from '@/components/organisms/Modals/UnsavedChangesModal/UnsavedChangesModal';
import { Button } from '@/components/ui/button';
import FileUpload from '@/components/ui/custom/FileUpload/FileUpload';
import useDatasources from '@/hooks/useDatasources';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { useTrackFormState } from '@/hooks/useTrackFormState';
import { getDebouncedInvalidator } from '@/hooks/utils/debounceQueryInvalidation';
import { setDatasourceId, setDocumentId } from '@/redux/session/actions';
import { selectAccountId } from '@/redux/session/selectors';
import { setSidebarContent } from '@/redux/sidebar/actions';
import { DatasourceType } from '@/shared/types/collections';
import { FIFTEEN_MB, FILES_BASE_URL } from '@/util/constants';
import { extractNames, generateUniqueName } from '@/util/util';
import { LENGTH_XS } from '@/util/validation/constants';
import { createNameFieldSchema } from '@/util/validation/schema-builders';

import { CreateFolderCard } from './CreateFolderCard';
import {
  MAX_TXT_PREVIEW_LENGTH,
  ROOT_DATASOURCE_NAMES,
  ROOT_DATASOURCE_TYPES,
} from '../../constants';
import { useAPIDocuments } from '../../hooks/useAPIDocuments';
import { computeTxtPreview } from '../../utils/preview';

export const defaultTitle = 'Untitled_folder';

const exampleCsv = [
  ['question', 'answer'],
  ['What is your return policy?', 'You can return any item within 30 days of purchase.'],
  [
    'How do I reset my password?',
    'Click on "Forgot password" at login and follow the instructions.',
  ],
  ['Do you offer international shipping?', 'Yes, we ship to most countries worldwide.'],
  ['How can I contact support?', 'You can contact us via email at support@example.com.'],
] as const;

export const UploadFAQs = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [hasUnsavedFiles, setHasUnsavedFiles] = useState(false);
  const [validFiles, setValidFiles] = useState<File[]>([]);
  const [parsedFilesData, setParsedFilesData] = useState<
    Map<string, { question: string; answer: string }[]>
  >(new Map());
  // Redux selectors
  const accountId = useSelector(selectAccountId);
  const queryClient = useQueryClient();
  const newFolderDatasourceIdRef = useRef<string | null>(null);

  const [showNewFolder, setShowNewFolder] = useState(false);
  // Hooks
  const { collectionId, datasourceId } = useParams();

  // Create schema with dynamic name validation
  const formSchema = yup.object({
    title: createNameFieldSchema({
      getEndpoint: (name) =>
        datasourceEndpoints.checkName(collectionId, name, { collection_id: collectionId }),
      max: LENGTH_XS,
    }),
  });

  type FormValues = yup.InferType<typeof formSchema>;
  const form = useForm<FormValues>({
    resolver: yupResolver<FormValues, unknown, FormValues>(formSchema),
    mode: 'onChange',
    defaultValues: { title: defaultTitle },
  });
  const { uploadFileToUrl } = useFaqSubmit();
  const { datasources, createDatasourceAsync } = useDatasources(collectionId);
  const { createDocument, data: documents } = useAPIDocuments({
    collection_id: collectionId,
    datasource_id: datasourceId,
  });
  const isInsideFolder = !!datasourceId;

  const parseCSV = (file: File): Promise<{ question: string; answer: string }[]> => {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        complete: (results) => {
          // Validate CSV structure
          if (
            !results.meta.fields?.includes('question') ||
            !results.meta.fields?.includes('answer')
          ) {
            reject(
              new Error(
                'Invalid CSV format. Please ensure your file has "question" and "answer" columns.'
              )
            );
            return;
          }

          // Filter out empty rows and validate data
          const validData = results.data
            .filter((row) => row.question && row.answer)
            .map((row) => ({ question: row.question.trim(), answer: row.answer.trim() }));

          if (validData.length === 0) {
            reject(new Error('CSV file contains no valid FAQ pairs.'));
            return;
          }

          resolve(validData);
        },
        error: (error) => {
          reject(new Error(`Failed to parse CSV: ${error.message}`));
        },
      });
    });
  };

  /**
   * Uploads a FAQ pair (question/answer) as a file and creates a document in the correct datasource.
   * @param datasourceId - The target datasource ID
   * @param question - The FAQ question (used as file/document name)
   * @param answer - The FAQ answer (file content)
   */
  async function saveFAQToDatasource(datasourceId: string, question: string, answer: string) {
    const qaFile = new File([answer], question, { type: 'text/plain' });
    try {
      const response = await uploadFileToUrl(
        qaFile,
        `${FILES_BASE_URL}/${accountId}/${collectionId}/${datasourceId}`
      );
      const preview = computeTxtPreview(answer, MAX_TXT_PREVIEW_LENGTH);
      createDocument({
        datasource_id: datasourceId,
        type: 'txt',
        name: generateUniqueName(question, extractNames(documents)),
        file_url: response.url,
        metadata: { preview },
      });
    } catch (error) {
      throw new Error(`Failed to upload FAQ with error: ${error}`);
    }
  }

  const handleUploadClick = async () => {
    if (showNewFolder && !isInsideFolder) {
      const uniqueName = generateUniqueName(
        form.getValues('title') || defaultTitle,
        extractNames(datasources ?? [])
      );

      const datasource = await createDatasourceAsync({
        type: DatasourceType.FAQ,
        name: uniqueName,
        config: {},
      });
      newFolderDatasourceIdRef.current = datasource.datasource_id;
    }
  };
  /**
   * Handles uploading all FAQ CSV files and creating documents, mimicking AddFAQNew.tsx logic.
   */
  const handleFileUpload = async () => {
    try {
      // For each file, for each QA pair
      for (const file of validFiles) {
        const qaData = parsedFilesData.get(file.name);
        if (!qaData) {
          continue;
        }
        // For each QA pair, determine the correct datasource
        // (Always root FAQ for now, as in AddFAQ)
        const rootFAQDatasource = datasources?.find(
          (ds) => ds.type === DatasourceType.FAQ && ds.name === ROOT_DATASOURCE_NAMES.faq
        );
        let targetDatasourceId: string;
        if (newFolderDatasourceIdRef.current) {
          targetDatasourceId = newFolderDatasourceIdRef.current;
        } else if (isInsideFolder) {
          targetDatasourceId = datasourceId;
        } else if (rootFAQDatasource) {
          targetDatasourceId = rootFAQDatasource.datasource_id;
        } else {
          // Create root FAQ datasource
          const { datasource_id } = await createDatasourceAsync(
            ROOT_DATASOURCE_TYPES[DatasourceType.FAQ]
          );
          targetDatasourceId = datasource_id;
        }

        for (const { question, answer } of qaData) {
          await saveFAQToDatasource(targetDatasourceId, question, answer);
          // 1000ms delay between uploads
          await new Promise((resolve) => setTimeout(resolve, 1000));
        }
        newFolderDatasourceIdRef.current = null;
      }
      // Flush any pending invalidations to ensure UI updates
      const invalidator = getDebouncedInvalidator(queryClient);
      invalidator.flush();
      toast.success(t('collections.toast_messages.success_upload_qa'));
      setHasUnsavedFiles(false);
      setValidFiles([]);
      setParsedFilesData(new Map());
      // Reset the folder title to default
      form.reset({ title: defaultTitle });
      // Reset the show new folder toggle
      setShowNewFolder(false);
    } catch (error) {
      toast.error(error.message || t('collections.toast_messages.failed_to_process_csv'));
    }
  };

  const handleFilesChange = async (files: File[]) => {
    if (files.length === 0) {
      setHasUnsavedFiles(false);
      setValidFiles([]);
      setParsedFilesData(new Map());
      return;
    }

    const newValidFiles: File[] = [];
    const newParsedData = new Map<string, { question: string; answer: string }[]>();
    const errors: string[] = [];

    // Process each file
    for (const file of files) {
      try {
        const qaData = await parseCSV(file);
        newValidFiles.push(file);
        newParsedData.set(file.name, qaData);
      } catch (error) {
        // File is invalid, don't include it
        errors.push(`${file.name}: ${error.message}`);
      }
    }

    // Show consolidated error toast if there are any errors
    // Using setTimeout to avoid double toast in React StrictMode
    if (errors.length > 0) {
      setTimeout(() => {
        const errorMessage = `Failed to process ${errors.length} file(s):\n${errors.map((err) => `• ${err}`).join('\n')}`;
        toast.error(errorMessage);
      }, 0);
    }

    // Update state with only valid files
    setValidFiles(newValidFiles);
    setParsedFilesData(newParsedData);
    setHasUnsavedFiles(newValidFiles.length > 0);
  };

  const handleDownloadExampleCsv = (): void => {
    const csv = Papa.unparse(exampleCsv);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, 'example-qa.csv');
  };

  const resetReduxState = () => {
    dispatch(setDocumentId(null));
    dispatch(setDatasourceId(null));
  };

  // Restore handleClose, handleDiscardChanges, handleGoBack as local state handlers
  const handleClose = () => {
    if (hasUnsavedFiles) {
      setShowUnsavedModal(true);
    } else {
      dispatch(setSidebarContent(null));
      resetReduxState();
    }
  };

  const handleDiscardChanges = () => {
    setShowUnsavedModal(false);
    setHasUnsavedFiles(false);
    setValidFiles([]);
    setParsedFilesData(new Map());
    dispatch(setSidebarContent(null));
    resetReduxState();
  };

  const handleGoBack = () => {
    setShowUnsavedModal(false);
  };

  useTrackFormState({ isDirty: hasUnsavedFiles, formId: 'upload-qa' });
  // Close the sidebar when the Escape key is pressed
  useEscapeKey(handleClose);

  return (
    <SidebarWrapper>
      <SidebarWrapperTitle>
        <h2 className="text-base leading-5 font-medium">{t('collections.documents.upload_qa')}</h2>
        <Button variant="ghost-neutral" size="icon-sm" type="button" onClick={handleClose}>
          <XIcon size={16} className="text-foreground-muted" />
        </Button>
      </SidebarWrapperTitle>

      <div className="space-y-4 p-6">
        <FileUpload
          maxSizeMB={FIFTEEN_MB / 1000000}
          acceptedFormats={{ 'text/csv': ['.csv'] }}
          onUpload={handleFileUpload}
          onFilesChange={handleFilesChange}
          files={validFiles}
          onUploadClick={handleUploadClick}
          placeholder={t('file_upload.dnd_csv_file')}
          showAcceptedFormats={false}
        >
          {validFiles.length > 0 && !isInsideFolder && (
            <CreateFolderCard
              form={form}
              enabled={showNewFolder}
              onEnabledChange={setShowNewFolder}
            />
          )}
        </FileUpload>

        {validFiles.length === 0 && (
          <div className="flex justify-center py-4">
            <Button
              variant="outline-neutral"
              size="sm"
              type="button"
              onClick={handleDownloadExampleCsv}
            >
              <DownloadIcon size={16} />
              {t('collections.documents.download_template_file')}
            </Button>
          </div>
        )}
      </div>

      {showUnsavedModal && (
        <UnsavedChangesModal
          onOpenChange={setShowUnsavedModal}
          onDiscard={handleDiscardChanges}
          onGoBack={handleGoBack}
        />
      )}
    </SidebarWrapper>
  );
};
